import React, { useState, useEffect } from "react";
import {
  FiPackage,
  FiCheckCircle,
  FiTruck,
  FiBox,
  FiAlertCircle,
  FiCalendar,
  FiDollarSign,
  FiTrash2,
  FiClock,
  FiArchive,
} from "react-icons/fi";
import { useUser } from "@clerk/clerk-react";
import axios from "axios";
import { SERVER_URL } from "../../constants";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const statusSteps = [
  { status: "OrderPlaced", label: "Order Placed", icon: <FiPackage /> },
  { status: "Shipped", label: "Shipped", icon: <FiTruck /> },
  { status: "Out for Delivery", label: "Out for Delivery", icon: <FiBox /> },
  { status: "Delivered", label: "Delivered", icon: <FiCheckCircle /> },
];

const DeleteConfirmationModal = ({ isOpen, onClose, onConfirm }) => {
  if (!isOpen) return null;
  return (
    <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-2xl p-6 max-w-md w-full shadow-2xl transform transition-all duration-300 scale-100">
        <h3 className="text-2xl font-bold text-gray-800 mb-4">Confirm Deletion</h3>
        <p className="text-gray-600 mb-6">Are you sure you want to delete this order?</p>
        <div className="flex justify-end gap-4">
          <button
            onClick={onClose}
            className="px-5 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors duration-200"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            className="px-5 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors duration-200"
          >
            Delete
          </button>
        </div>
      </div>
    </div>
  );
};

const TrackOrder = ({ orderId, onClose }) => {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchOrderStatus();
    const interval = setInterval(fetchOrderStatus, 5000);
    return () => clearInterval(interval);
  }, [orderId]);

  const fetchOrderStatus = async () => {
    try {
      const res = await axios.get(`${SERVER_URL}/api/v1/ordertrack/${orderId}`);
      if (res.data.m === "f" || !res.data.data) {
        toast.error("Order not found");
        return;
      }
      setStatus(res.data.data.delivaryStatus);
    } catch (error) {
      console.error("Error fetching order status:", error);
      toast.error("Server error while fetching order status");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-2xl p-6 max-w-md w-full shadow-2xl transform transition-all duration-300 scale-100">
        <h3 className="text-2xl font-bold text-gray-800 mb-4">Track Your Order</h3>
        {loading ? (
          <p className="text-gray-500 flex items-center gap-2">
            <FiClock className="animate-spin" /> Loading...
          </p>
        ) : (
          <div className="space-y-6">
            {statusSteps.map((step, index) => (
              <div key={index} className="flex items-center gap-4">
                <div
                  className={`w-12 h-12 flex items-center justify-center rounded-full transition-all duration-300 ${
                    step.status === status || statusSteps.findIndex((s) => s.status === status) >= index
                      ? "bg-gradient-to-r from-green-500 to-teal-500 text-white shadow-lg"
                      : "bg-gray-200 text-gray-600"
                  }`}
                >
                  {step.icon}
                </div>
                <span className="text-lg font-medium text-gray-700">{step.label}</span>
              </div>
            ))}
          </div>
        )}
        <button
          onClick={onClose}
          className="mt-6 w-full px-4 py-2 bg-gradient-to-r from-red-500 to-red-600 text-white rounded-lg hover:from-red-600 hover:to-red-700 transition-all duration-200 shadow-md"
        >
          Close
        </button>
      </div>
    </div>
  );
};

const OrderItem = ({ order, onDelete, isHistory }) => {
  const [showTrackModal, setShowTrackModal] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const handleDelete = async () => {
    try {
      await axios.delete(`${SERVER_URL}/api/v1/orderd/${order._id}`);
      toast.success("Order deleted successfully");
      onDelete();
    } catch (error) {
      toast.error("Failed to delete order");
    }
    setShowDeleteConfirm(false);
  };

  const isDelivered = order.delivaryStatus === "Delivered";

  // Calculate expected delivery date
  const orderDate = new Date(order.createdAt);
  const today = new Date();
  const initialExpectedDate = new Date(orderDate);
  initialExpectedDate.setDate(orderDate.getDate() + 2); // Initial "next 2 days"

  // Dynamically adjust if not delivered and initial date has passed
  let expectedDeliveryDate = initialExpectedDate;
  if (!isDelivered && today > initialExpectedDate) {
    const daysPast = Math.ceil((today - initialExpectedDate) / (1000 * 60 * 60 * 24));
    expectedDeliveryDate = new Date(initialExpectedDate);
    expectedDeliveryDate.setDate(initialExpectedDate.getDate() + daysPast);
  }

  const formatDate = (date) => date.toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric" });

  return (
    <>
      <div className="relative group">
        {isDelivered && isHistory && (
          <div className="absolute -top-4 -right-4 bg-gradient-to-r from-green-500 to-teal-500 text-white rounded-full p-2 shadow-lg z-10 animate-pulse">
            <FiCheckCircle className="w-6 h-6" />
          </div>
        )}
        <div
          className={`p-6 border rounded-2xl shadow-lg mb-6 transition-all duration-300 transform ${
            isDelivered && isHistory
              ? "bg-gradient-to-br from-gray-200 to-gray-300 opacity-90 scale-95"
              : "bg-white hover:shadow-xl hover:-translate-y-1"
          }`}
        >
          <div className="flex flex-col md:flex-row items-start md:items-center gap-6">
            <div className="w-32 h-32 overflow-hidden rounded-xl shadow-md relative">
              <img
                src={order.image}
                alt={order.name}
                className={`w-full h-full object-cover transform transition-transform duration-300 ${
                  isDelivered && isHistory ? "filter grayscale opacity-70" : "group-hover:scale-105"
                }`}
              />
              {isDelivered && isHistory && (
                <div className="absolute inset-0 flex items-center justify-center">
                  <span className="text-white text-sm font-semibold bg-black bg-opacity-50 px-2 py-1 rounded-full">
                    Delivered
                  </span>
                </div>
              )}
            </div>
            <div className="flex-1">
              <h3
                className={`text-xl font-bold ${
                  isDelivered && isHistory ? "text-gray-600" : "text-gray-800"
                }`}
              >
                {order.name} ×{order.quantity}
              </h3>
              <div className="flex flex-wrap gap-4 mt-3">
                <div
                  className={`flex items-center ${
                    isDelivered && isHistory ? "text-gray-500" : "text-gray-600"
                  }`}
                >
                  <FiCalendar
                    className={`mr-2 ${isDelivered && isHistory ? "text-gray-400" : "text-teal-500"}`}
                  />
                  Ordered: {new Date(order.createdAt).toLocaleDateString()}
                </div>
                <div
                  className={`flex items-center ${
                    isDelivered && isHistory ? "text-gray-500" : "text-gray-600"
                  }`}
                >
                  <FiDollarSign
                    className={`mr-2 ${isDelivered && isHistory ? "text-gray-400" : "text-teal-500"}`}
                  />
                  ₹{order.totalAmount.toFixed(2)}
                </div>
                <div
                  className={`flex items-center ${
                    isDelivered && isHistory ? "text-gray-500" : "text-gray-600"
                  }`}
                >
                  <FiAlertCircle
                    className={`mr-2 ${isDelivered && isHistory ? "text-gray-400" : "text-teal-500"}`}
                  />
                  ID: {order._id.slice(-8)}
                </div>
              </div>
              {!isHistory && (
                <div className="mt-3 text-sm">
                  <span className="font-semibold text-gray-700">Expected Delivery: </span>
                  <span className="text-green-600 font-medium">{formatDate(expectedDeliveryDate)}</span>
                </div>
              )}
            </div>
            {!isHistory && (
              <div className="flex flex-col sm:flex-row gap-3">
                <button
                  onClick={() => setShowTrackModal(true)}
                  className="px-6 py-2 bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-lg hover:from-blue-600 hover:to-blue-700 transition-all duration-200 shadow-md flex items-center gap-2"
                >
                  <FiTruck /> Track
                </button>
                <button
                  onClick={() => setShowDeleteConfirm(true)}
                  className="px-6 py-2 bg-gradient-to-r from-red-500 to-red-600 text-white rounded-lg hover:from-red-600 hover:to-red-700 transition-all duration-200 shadow-md flex items-center gap-2"
                >
                  <FiTrash2 /> Delete
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
      {showTrackModal && (
        <TrackOrder orderId={order._id} onClose={() => setShowTrackModal(false)} />
      )}
      <DeleteConfirmationModal
        isOpen={showDeleteConfirm}
        onClose={() => setShowDeleteConfirm(false)}
        onConfirm={handleDelete}
      />
    </>
  );
};

const OrderHistory = () => {
  const { user } = useUser();
  const [orders, setOrders] = useState([]);
  const [activeTab, setActiveTab] = useState("current");

  useEffect(() => {
    fetchOrders();
  }, [user?.id]);

  const fetchOrders = async () => {
    try {
      const res = await axios.get(`${SERVER_URL}/api/v1/orderu/${user.id}`);
      const sortedOrders = (res.data.data || []).sort(
        (a, b) => new Date(b.createdAt) - new Date(a.createdAt)
      );
      setOrders(sortedOrders);
    } catch (error) {
      toast.error("Failed to fetch orders");
    }
  };

  const currentOrders = orders.filter((order) => order.delivaryStatus !== "Delivered");
  const allOrders = orders;

  return (
    <div className="min-h-screen bg-gray-100 p-6">
      <ToastContainer position="top-right" autoClose={3000} />
      <div className="max-w-6xl mx-auto">
        <h1 className="text-4xl font-extrabold text-gray-800 mb-8 bg-gradient-to-r from-teal-500 to-blue-500 bg-clip-text text-transparent">
          Your Order Journey
        </h1>

        {/* Tabs */}
        <div className="flex justify-center mb-8">
          <div className="inline-flex bg-white rounded-full shadow-lg p-1 border border-gray-200">
            <button
              onClick={() => setActiveTab("current")}
              className={`px-6 py-2 rounded-full flex items-center gap-2 text-sm font-medium transition-all duration-300 ${
                activeTab === "current"
                  ? "bg-gradient-to-r from-teal-500 to-blue-500 text-white shadow-md"
                  : "text-gray-600 hover:text-teal-500"
              }`}
            >
              <FiClock /> Current Orders ({currentOrders.length})
            </button>
            <button
              onClick={() => setActiveTab("all")}
              className={`px-6 py-2 rounded-full flex items-center gap-2 text-sm font-medium transition-all duration-300 ${
                activeTab === "all"
                  ? "bg-gradient-to-r from-teal-500 to-blue-500 text-white shadow-md"
                  : "text-gray-600 hover:text-teal-500"
              }`}
            >
              <FiArchive /> All Orders ({allOrders.length})
            </button>
          </div>
        </div>

        {/* Orders List */}
        <div className="space-y-6">
          {activeTab === "current" ? (
            currentOrders.length === 0 ? (
              <div className="text-center py-12 bg-white rounded-2xl shadow-lg">
                <p className="text-lg text-gray-600">No current orders yet! Start shopping now!</p>
              </div>
            ) : (
              currentOrders.map((order) => (
                <OrderItem key={order._id} order={order} onDelete={fetchOrders} isHistory={false} />
              ))
            )
          ) : allOrders.length === 0 ? (
            <div className="text-center py-12 bg-white rounded-2xl shadow-lg">
              <p className="text-lg text-gray-600">No order history yet! Start shopping now!</p>
            </div>
          ) : (
            allOrders.map((order) => (
              <OrderItem key={order._id} order={order} onDelete={fetchOrders} isHistory={true} />
            ))
          )}
        </div>
      </div>
    </div>
  );
};

export default OrderHistory;
